import { StyleSheet, View } from "react-native";
import { useAppConfig } from "../../../AppConfigProvider";
import {
  useDeleteAccountMutation,
  useExportCsvQuery,
  useGetCoursesForTranscriptGenerationQuery,
  useMeQuery,
  useResetAccountMutation,
} from "../../../graphql/generated/graphql";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { usePostHog } from "posthog-js/react";
import { useState, useEffect } from "react";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useVisitSession } from "../../../contexts/VisitSessionContext";
import { useAuth } from "../../../hooks/useAuth";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { exportConversationTranscript } from "../ProfileTabContainer/exportConversationTranscript";
import { SettingFieldWrapper } from "./SettingFieldWrapper";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { shadowStyles } from "../../../styles/shadow";
import DeleteAccountConfirmationModal from "../../../components/settings/DeleteAccountConfirmationModal/DeleteAccountConfirmationModal";
import ExportAccountConfirmationModal from "../../../components/settings/ExportAccountConfirmationModal/ExportAccountConfirmationModal";
import ResetAccountConfirmationModal from "../../../components/settings/ResetAccountConfirmationModal/ResetAccountConfirmationModal";
import DeleteAccountModalContainer from "../ProfileTabContainer/DeleteAccountModalContainer/DeleteAccountModalContainer";
import ExportAccountModalContainer from "../ProfileTabContainer/ExportAccountModalContainer/ExportAccountModalContainer";
import ResetAccountModalContainer from "../ProfileTabContainer/ResetAccountModalContainer/ResetAccountModalContainer";
import TwoFactorAuthDisableContainer from "../ProfileTabContainer/TwoFactorAuthModalContainer/TwoFactorAuthDisableContainer";
import TwoFactorAuthModalContainer from "../ProfileTabContainer/TwoFactorAuthModalContainer/TwoFactorAuthModalContainer";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useToast } from "../../../contexts/Toast/ToastProvider";

enum ModalState {
  NONE = "NONE",
  DELETE = "DELETE",
  RESET = "RESET",
  EXPORT = "EXPORT",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  RESET_CONFIRMATION = "RESET_CONFIRMATION",
  EXPORT_CONFIRMATION = "EXPORT_CONFIRMATION",
  TWO_FACTOR_AUTH = "TWO_FACTOR_AUTH",
  TWO_FACTOR_DISABLE = "TWO_FACTOR_DISABLE",
}

export const UserData = () => {
  const { dispatch } = useAppState();
  const posthog = usePostHog();
  const appConfig = useAppConfig();
  const { showToast } = useToast();
  const { isLarge } = useResponsiveScreen();
  const isLTI = appConfig?.isLTI;
  const { logout, meData, refetchMe } = useAuth();
  const { endSession } = useVisitSession();
  const [modalState, setModalState] = useState(ModalState.NONE);
  const [deleteAccount] = useDeleteAccountMutation();
  const [resetAccount] = useResetAccountMutation();
  const { refetch: exportCsvRefetch } = useExportCsvQuery({
    skip: true,
  });

  const schoolId = appConfig.axioSchools[0];

  const { data: exportData } = useGetCoursesForTranscriptGenerationQuery({
    variables: {
      schoolId,
    },
    skip: !schoolId,
  });

  const handleLogout = async () => {
    await endSession();
    await logout();
  };

  const handleDeleteAccount = async (typedText: string) => {
    if (typedText === "DELETE") {
      try {
        await AsyncStorage.setItem("deletionInProgress", "true");

        const { data: deleteAccountData } = await deleteAccount();
        if (deleteAccountData?.deleteAccount?.success) {
          setModalState(ModalState.DELETE_CONFIRMATION);
        } else {
          showToast({
            message: deleteAccountData?.deleteAccount?.message || "Failed to delete account.",
            type: "error",
          });
        }
      } catch (error) {
        showToast({
          message: "An error occurred. Please try again.",
          type: "error",
        });
      }
    } else {
      showToast({
        message: "You must type DELETE to confirm.",
        type: "warning",
      });
    }
  };

  const handleResetAccount = () => {
    resetAccount()
      .then(({ data: resetAccountData }) => {
        if (resetAccountData?.resetAccount?.success) {
          setModalState(ModalState.RESET_CONFIRMATION);
        } else {
          showToast({
            message: resetAccountData?.resetAccount?.message || "Failed to reset account.",
            type: "error",
          });
        }
      })
      .catch(() => {
        showToast({
          message: "An error occurred. Please try again.",
          type: "error",
        });
      });
  };

  const handleExportAccount = () => {
    exportCsvRefetch()
      .then(({ data: exportCsvData }) => {
        if (exportCsvData?.exportCsv?.success) {
          const blob = new Blob([exportCsvData.exportCsv.csvData], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", "user_data.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setModalState(ModalState.EXPORT_CONFIRMATION);
        } else {
          showToast({
            message: exportCsvData?.exportCsv?.message || "Failed to export account data.",
            type: "error",
          });
        }
      })
      .catch(() => {
        showToast({
          message: "An error occurred. Please try again.",
          type: "error",
        });
      });
  };

  const handleModalOpen = (modalState: ModalState) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setModalState(modalState);
  };

  const handleModalClose = (callback?: () => void) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setModalState(ModalState.NONE);
    if (callback) {
      callback();
    }
  };

  const handleResetModalClose = (callback?: () => void) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setModalState(ModalState.NONE);
    window.location.reload(); // Refresh the site after reset
    if (callback) {
      callback();
    }
  };

  const handleDeleteAccountConfirmationCloseCb = async () => {
    await AsyncStorage.removeItem("deletionInProgress");
    await handleLogout();
  };

  useEffect(() => {
    const checkDeletionState = async () => {
      const deletionInProgress = await AsyncStorage.getItem("deletionInProgress");
      if (deletionInProgress === "true") {
        setModalState(ModalState.DELETE_CONFIRMATION);
      }
    };

    checkDeletionState();
    posthog?.capture("Settings Page Visited");
  }, []);

  const handleDownloadTranscript = async () => {
    await exportConversationTranscript(exportData?.getCoursesForTranscriptGeneration.data);
  };

  return (
    <View style={styles.container}>
      <CustomText
        style={styles.sectionHeaderTitle}
        text="Profile"
        accessibilityLabel="Profile"
        accessibilityRole="text"
        aria-level="2"
        role="heading"
      />
      <SettingFieldWrapper title={"Name"}>
        <CustomTextInput
          inputType={undefined}
          value={meData?.me?.name}
          onChangeText={undefined}
          placeholder={undefined}
          style={{ pointerEvents: "none" }}
        />
      </SettingFieldWrapper>
      {!isLTI && (
        <SettingFieldWrapper
          childrenSlot="in-line"
          title={"Two-factor Authentication"}
          subTitle={
            "Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to sign in."
          }>
          {meData?.me?.otpPreference === "NONE" ? (
            <CustomButton
              text="Enable Two-factor Authentication"
              styleType="primaryTransparent"
              style={[styles.managementButton, shadowStyles.smallShadowButton, { width: "fit-content" }]}
              onPress={() => handleModalOpen(ModalState.TWO_FACTOR_AUTH)}
              disabled={undefined}
              leftIcon={undefined}
              rightIcon={undefined}
              textStyle={undefined}
              textProps={undefined}
              size="s"
            />
          ) : (
            <CustomButton
              text="Turn off Two-factor Authentication"
              styleType="primaryTransparent"
              style={[styles.managementButton, { width: "fit-content" }]}
              onPress={() => handleModalOpen(ModalState.TWO_FACTOR_DISABLE)}
              disabled={undefined}
              leftIcon={undefined}
              rightIcon={undefined}
              textStyle={undefined}
              textProps={undefined}
              size="s"
            />
          )}
        </SettingFieldWrapper>
      )}
      <SettingFieldWrapper title={"Account Management"} childrenSlot="in-line">
        <View style={[styles.managementButtonsContainer, !isLarge && { flexDirection: "column" }]}>
          <CustomButton
            text="Export Account Data"
            styleType="primaryTransparent"
            style={[styles.managementButton, shadowStyles.smallShadowButton, { width: "fit-content" }]}
            onPress={() => handleModalOpen(ModalState.EXPORT)}
            size="s"
          />
          <CustomButton
            text="Reset Account"
            styleType="primaryTransparent"
            style={[styles.managementButton, shadowStyles.smallShadowButton, { width: "fit-content" }]}
            onPress={() => handleModalOpen(ModalState.RESET)}
            size="s"
          />
          <CustomButton
            text="Delete Account"
            styleType="primaryTransparent"
            style={[styles.managementButton, shadowStyles.smallShadowButton, { width: "fit-content" }]}
            onPress={() => handleModalOpen(ModalState.DELETE)}
            size="s"
          />
        </View>
      </SettingFieldWrapper>
      <DeleteAccountModalContainer
        visible={modalState === ModalState.DELETE}
        onClose={handleModalClose}
        onDelete={handleDeleteAccount}
      />
      <ResetAccountModalContainer
        visible={modalState === ModalState.RESET}
        onClose={handleModalClose}
        onReset={handleResetAccount}
      />
      <ExportAccountModalContainer
        visible={modalState === ModalState.EXPORT}
        onClose={handleModalClose}
        onExport={handleExportAccount}
      />
      <DeleteAccountConfirmationModal
        visible={modalState === ModalState.DELETE_CONFIRMATION}
        onClose={() => handleModalClose(handleDeleteAccountConfirmationCloseCb)}
      />
      <ResetAccountConfirmationModal
        visible={modalState === ModalState.RESET_CONFIRMATION}
        onClose={handleResetModalClose}
      />
      <ExportAccountConfirmationModal
        visible={modalState === ModalState.EXPORT_CONFIRMATION}
        onClose={handleModalClose}
      />
      <TwoFactorAuthModalContainer visible={modalState === ModalState.TWO_FACTOR_AUTH} onClose={handleModalClose} />
      <TwoFactorAuthDisableContainer
        visible={modalState === ModalState.TWO_FACTOR_DISABLE}
        onClose={handleModalClose}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    borderRadius: 32,
    padding: 24,
    borderWidth: 1,
    borderColor: "#eaecf0",
    backgroundColor: "#fff",
  },
  sectionHeaderTitle: {
    fontWeight: 600,
    fontSize: 16,
  },
  managementButton: {
    color: "#3E68FE",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  managementButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
});
